/** Mirrors cm-server-java `cmserver.systemupdate` — `SystemUpdateResponse`. */
export interface SystemUpdate {
  uuid: string;
  title: string;
  /** Plain text; may carry `[[page_key]]` markers (see system-pages.config.ts). */
  body: string;
  mediaAssetUuid: string | null;
  /** `video` / `image`; null when the update has no media. */
  mediaType: string | null;
  /** Media-asset file name, relative to `/mediaassets/` (use `systemUpdateMediaSrc`). */
  mediaUrl: string | null;
  /** Only set for `.mp4`. */
  mediaThumbnailUrl: string | null;
  published: boolean;
  publishedAt: string | null;
  createdByUuid: string | null;
  createdAt: string;
  updatedAt: string;
  /** Whether the calling user has a read receipt — meaningful on the archive endpoint only. */
  read: boolean;
  /** Number of users who read it — filled by the `/admin*` endpoints only (null elsewhere). */
  readCount: number | null;
  /**
   * Role codes (`RoleEnum` values) the update is for; empty = everyone. The reader endpoints already
   * return only what the caller may see, so readers never filter on it.
   */
  audienceRoles: string[];
}

/** A table row: `cm-ui-table` needs `row.id`, the API only has `uuid`. */
export interface SystemUpdateRow extends SystemUpdate {
  id: string;
}

/** An archive-table row: `media` drives the media column's icon (null = words only). */
export interface SystemUpdateArchiveRow extends SystemUpdateRow {
  media: 'video' | 'image' | null;
}

/** Mirrors `SystemUpdateReadResponse`. */
export interface SystemUpdateRead {
  userUuid: string;
  /** Null when the user no longer exists. */
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  readAt: string;
}

/** A viewers-table row: `id` for `cm-ui-table`, `name` joined from first + last name. */
export interface SystemUpdateReadRow extends SystemUpdateRead {
  id: string;
  name: string;
}

/** Spring `Page<T>` — only the fields the admin panel reads. */
export interface SystemUpdatePage {
  content: SystemUpdate[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

/**
 * Body of `POST /admin` and `PUT /admin/{uuid}` (`SystemUpdateRequest`). PUT replaces every field,
 * so always send all four: `mediaAssetUuid: null` clears the media, `audienceRoles: []` makes the
 * update for everyone again.
 */
export interface SystemUpdateSaveRequest {
  title: string;
  body: string;
  mediaAssetUuid: string | null;
  /** Role codes the update is for; `[]` = everyone. Java rejects unknown codes with 400. */
  audienceRoles: string[];
}

const VIDEO_EXTENSION = /\.(mp4|webm|mov|m4v)(\?.*)?$/i;

/**
 * Video or image? The API's `mediaType` decides (`video`, `personal_video`); without it the file
 * extension does — the Media Library stores videos as mp4.
 */
export function isSystemUpdateVideo(
  mediaUrl: string | null | undefined,
  mediaType?: string | null,
): boolean {
  if (mediaType) {
    return mediaType.toLowerCase().includes('video');
  }
  return !!mediaUrl && VIDEO_EXTENSION.test(mediaUrl);
}

/**
 * `mediaUrl` / `mediaThumbnailUrl` are file names inside the tenant media store, served by nginx at
 * `/mediaassets/{file}`. An already absolute URL (or a path) is left alone.
 */
export function systemUpdateMediaSrc(file: string | null | undefined): string | null {
  if (!file) {
    return null;
  }
  if (/^(https?:)?\/\//i.test(file) || file.startsWith('/') || file.startsWith('blob:') || file.startsWith('data:')) {
    return file;
  }
  return `/mediaassets/${file}`;
}

/** Joins first and last name for display; empty when the user no longer exists. */
export function systemUpdateReaderName(read: Pick<SystemUpdateRead, 'firstName' | 'lastName'>): string {
  return [read.firstName, read.lastName].filter((part) => !!part?.trim()).join(' ');
}

/** Does the update carry a film or a picture? */
export function systemUpdateMediaKind(
  update: Pick<SystemUpdate, 'mediaUrl' | 'mediaType'>,
): 'video' | 'image' | null {
  if (!update.mediaUrl) {
    return null;
  }
  return isSystemUpdateVideo(update.mediaUrl, update.mediaType) ? 'video' : 'image';
}
